import React, {
	memo,
	useCallback,
	useMemo,
	useRef,
	useEffect,
	useState,
} from "react";
import { Form, Button, Input, Row, Col, message, Modal, Upload } from "antd";
import {
	YoutubeOutlined,
	PictureOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { DatePicker } from "antd";
import { MentionsInput, Mention, SuggestionDataItem } from "react-mentions";
import classNames from "classnames";
import moment from "moment";

import styles from "./styles.module.scss";
import { IPostState, TStore } from "models";
import { CheckboxGroupView } from "components";
import { addPost, setPost } from "store";
import { disabledDate, parseContentWithMentions, rollbar } from "utils";
import RichPostEditor, {
	quillToStored,
	storedToQuill,
} from "components/RichPostEditor";
import { api } from "store";
import EnablePostForm from "./enablePostForm";
import { limitImageSelection } from "./imageSelectionLimit";

const TITLE_MAX_NO_OF_CHARACTERS = 50;
const DESCRIPTION_MAX_NO_OF_CHARACTERS = 5000;

const stateSelectorHandle = createSelector(
	(state: TStore) => state.community.data,
	(state: TStore) => state.community.mainTags,
	(state: TStore) => state.groups.data,
	(state: TStore) => state.members.users,
	(state: TStore) => state.post.data,
	(state: TStore) => state.app.currentGroupId,
	(state: TStore) => state.auth?.credentials?.user?.membership_type,
	(
		community,
		communityTags,
		groups,
		members,
		post,
		currentGroupId,
		authUserMembership,
	) => ({
		community,
		communityTags,
		groups: groups.filter((group) => group.is_joined),
		members,
		post,
		currentGroupId,
		authUserMembership,
	}),
);

interface IFormBox {
	hideEnablePostFormComponent?: boolean;
	uniqueKey: string;
	editPostData?: IPostState["data"];
	hidePostForm?: () => void;
}

const PostForm: React.FunctionComponent<IFormBox> = ({
	hideEnablePostFormComponent = false,
	hidePostForm,
	uniqueKey,
	editPostData,
}) => {
	const dispatch = useDispatch();
	const contentRef = useRef<HTMLDivElement | null>(null);
	const tagSectionRef = useRef<HTMLDivElement | null>(null);
	const [showPostForm, setShowPostForm] = useState(
		hideEnablePostFormComponent,
	);
	const [
		descriptionNoOfCharactersRemaining,
		setDescriptionNoOfCharactersRemaining,
	] = useState(DESCRIPTION_MAX_NO_OF_CHARACTERS);
	const [titleNoOfCharactersRemaining, setTitleNoOfCharactersRemaining] =
		useState(TITLE_MAX_NO_OF_CHARACTERS);
	const suggestionsPortalHostRef = useRef<any>();
	// Existing photos on an edited post (#1279): all are shown, each removable.
	const [existingImages, setExistingImages] = useState<
		{ id: number; image: string }[]
	>([]);
	const [deletedImageIds, setDeletedImageIds] = useState<number[]>([]);
	const [form] = Form.useForm();
	const watch = Form.useWatch("audienceCheckboxGroup", form);
	const watchTags = Form.useWatch("tagsCheckboxGroup", form);
	// Newly-selected photos (antd Upload fileList) — watched so we can render a
	// mobile-style preview instead of antd's tiny default picture-card list.
	const watchImages = Form.useWatch("image", form);
	// Explicit media toggle (Photos vs Video link), mirroring the admin panel.
	const [mediaType, setMediaType] = useState<"image" | "video">("image");
	// Keep the modal open with a loading indicator while the post saves.
	const [submitting, setSubmitting] = useState(false);
	const [tagValidationMessage, setTagValidationMessage] = useState<string | null>(
		null,
	);
	const postLoading = useSelector((state: TStore) => state.post.loading);
	const prevPostLoading = useRef(false);

	// #1448 Clear the "Post description is required" error as soon as a photo is
	// attached. antd doesn't re-run the details validator when the image field
	// changes, so without this the message lingers even though the post is
	// publishable. Only fires when images exist, so the error never shows on a
	// fresh empty form.
	useEffect(() => {
		const hasImages =
			(watchImages?.length || 0) > 0 || existingImages.length > 0;
		if (hasImages) {
			form.validateFields(["details"]).catch(() => {});
		}
	}, [watchImages, existingImages, form]);

	useEffect(() => {
		if ((watchTags?.length ?? 0) > 0) {
			setTagValidationMessage(null);
		}
	}, [watchTags]);

	const stateSelector = useCallback(stateSelectorHandle, []);
	const {
		community,
		communityTags,
		groups,
		members,
		currentGroupId,
		authUserMembership,
	} = useSelector(stateSelector);

	// Per-community photo limit (#1279), surfaced via community features.
	const maxImages = (community as any)?.features?.max_feed_post_photos ?? 5;

	const users = useMemo(() => {
		return members?.map((item) => {
			return { id: item.id, display: item.full_name };
		});
	}, [members]);

	const handleShowPostForm = useCallback(() => {
		setShowPostForm(true);
	}, []);

	const handleHidePostForm = useCallback(() => {
		setShowPostForm(false);
		hidePostForm && hidePostForm();
		dispatch(setPost({}));
		setTitleNoOfCharactersRemaining(TITLE_MAX_NO_OF_CHARACTERS);
		setDescriptionNoOfCharactersRemaining(DESCRIPTION_MAX_NO_OF_CHARACTERS);
		setExistingImages([]);
		setDeletedImageIds([]);
		setMediaType("image");
		setTagValidationMessage(null);
		form.resetFields();
	}, [dispatch, form, hidePostForm]);

	// While submitting, keep the modal open with a loading indicator; close it
	// once the save saga flips `post.loading` back to false (success or error).
	useEffect(() => {
		if (submitting && prevPostLoading.current && !postLoading) {
			setSubmitting(false);
			handleHidePostForm();
		}
		prevPostLoading.current = postLoading;
	}, [postLoading, submitting, handleHidePostForm]);

	const handleUploadHtmlImage = useCallback(
		async (file: File): Promise<string> => {
			if (!community?.id) throw new Error("Missing community");
			// Tell the user the image is uploading (the explicit ask) — kept
			// open (duration 0) until the upload resolves or fails.
			const hideLoading = message.loading("Uploading image…", 0);
			try {
				const { data } = await api.posts.uploadHtmlImage({
					community_id: community.id,
					file,
				});
				return data.url;
			} catch (e) {
				message.error("Couldn't upload that image. Please try again.");
				throw e;
			} finally {
				hideLoading();
			}
		},
		[community?.id],
	);

	const onFinish = useCallback(
		(values) => {
			try {
				if (community?.id) {
					const {
						title,
						details,
						link,
						audienceCheckboxGroup,
						groupsCheckboxGroup,
						tagsCheckboxGroup,
						expiryDate,
						image,
					} = values;

					//parse values
					let payload = {
						community_id: community.id, // TODO check this value on refresh (after logout)
						community_post: {
							title: title,
							description: quillToStored(details),
							pin_type: 0, // community post
							video_link: link,
							group_ids: currentGroupId
								? [currentGroupId]
								: groupsCheckboxGroup, // integers ids
							tag_list: tagsCheckboxGroup, // string value for example: "Accounting"
							member_type_list: audienceCheckboxGroup, // string value for example: "Accounting"
							expiration_date: expiryDate,
							// All selected photos (#1279), uploaded after create.
							images: image
								? image
										.map((f: any) => f?.originFileObj)
										.filter(Boolean)
								: [],
							all_groups: currentGroupId
								? false
								: !groupsCheckboxGroup ||
								  groupsCheckboxGroup?.length === 0
								? true
								: false,
						},
						communityPostId: null as null | number,
						// Only the images the user explicitly removed (#1279).
						delete_images: deletedImageIds,
					};
					if (editPostData?.community_post) {
						payload.communityPostId =
							editPostData.community_post.id;
					}
					// Keep the modal open and show a loading state while the
					// post (and any image uploads) save; the effect watching
					// `postLoading` closes it once the saga finishes.
					setSubmitting(true);
					dispatch(addPost(payload));
				} else {
					throw Error("Missing community data.");
				}
			} catch (error) {
				rollbar.error(error);
				console.error({ error });
			}
		},
		[
			deletedImageIds,
			community?.id,
			currentGroupId,
			dispatch,
			handleHidePostForm,
			editPostData?.community_post,
		],
	);

	const handleFinishFailed = useCallback(
		({ errorFields }: { errorFields: { name: (string | number)[] }[] }) => {
			const hasTagError = errorFields.some(
				({ name }) => name[0] === "tagsCheckboxGroup",
			);
			if (!hasTagError) {
				setTagValidationMessage(null);
				return;
			}

			setTagValidationMessage("Choose at least one tag before publishing.");

			requestAnimationFrame(() => {
				tagSectionRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});
				form.scrollToField("tagsCheckboxGroup", {
					behavior: "smooth",
					block: "center",
				});
			});
		},
		[form],
	);

	const onValuesChange = useCallback(
		(changedValues) => {
			switch (Object.keys(changedValues)[0]) {
				case "audienceCheckAll": {
					form.setFieldsValue({
						audienceCheckboxGroup: communityTags.memberTypes.map(
							(item: any) => item.name,
						),
					});
					break;
				}
				case "audienceCheckNone": {
					form.setFieldsValue({
						audienceCheckboxGroup: [authUserMembership],
					});
					break;
				}
				case "groupsCheckAll": {
					form.setFieldsValue({
						groupsCheckboxGroup: groups.map((item: any) => item.id),
					});

					break;
				}
				case "groupsCheckNone": {
					form.setFieldsValue({
						groupsCheckboxGroup: [],
					});

					break;
				}
				case "audienceCheckboxGroup": {
					if (
						changedValues.audienceCheckboxGroup.length ===
						communityTags.memberTypes.length
					) {
						form.setFieldsValue({
							audienceCheckAll: true,
						});
					} else {
						form.setFieldsValue({
							audienceCheckAll: false,
						});
					}
					break;
				}
				case "groupsCheckboxGroup": {
					if (
						changedValues.groupsCheckboxGroup.length ===
						groups.length
					) {
						form.setFieldsValue({
							groupsCheckAll: true,
						});
					} else {
						form.setFieldsValue({
							groupsCheckAll: false,
						});
					}

					break;
				}
				case "expiryDate": {
					if (moment.isMoment(changedValues.expiryDate)) {
						form.setFieldsValue({
							expiryDate: changedValues.expiryDate.endOf("day"),
						});
					} else {
						form.setFieldsValue({
							expiryDate: undefined,
						});
					}
					break;
				}
				default:
			}
		},
		[form, communityTags.memberTypes, authUserMembership, groups],
	);

	const handleRenderSuggestions = useCallback(
		(
			suggestion: SuggestionDataItem,
			search: string,
			highlightedDisplay: React.ReactNode,
			index: number,
			focused: boolean,
		) => {
			return (
				<div
					key={`key_${index}`}
					className={classNames(styles.listItem, {
						[styles.listItemFocused]: focused,
					})}
				>
					{highlightedDisplay}
				</div>
			);
		},
		[],
	);

	const handleChangePostBody = useCallback(
		(event: { target: { value: string } } | undefined) => {
			if (!event) return;
			let parsedBody = event.target.value;
			if (event.target.value.length > 5000) {
				parsedBody = event.target.value.substring(0, 5000);
			}
			setDescriptionNoOfCharactersRemaining(5000 - parsedBody.length);

			form.setFieldsValue({
				details: parsedBody,
			});
		},
		[form],
	);

	const handleChangePostTitle = useCallback(
		(event: React.ChangeEvent<HTMLInputElement> | undefined) => {
			if (!event) return null;
			setTitleNoOfCharactersRemaining(50 - event.target.value.length);
			form.setFieldsValue({
				title: event.target.value, // source
			});
		},
		[form],
	);

	const initialValues = useMemo(
		() => ({
			audienceCheckAll: false,
			groupsCheckboxGroup: [],
			details: "",
		}),
		[],
	);

	// antd Upload stores its value as a fileList; cap each picker selection
	// against both saved and newly-selected photos, and report any overflow.
	const normFile = (event: any) => {
		const selectedFiles = Array.isArray(event)
			? event
			: event?.fileList || [];
		const result = limitImageSelection(
			selectedFiles,
			existingImages.length,
			maxImages,
		);

		if (result.rejectedCount > 0) {
			message.error({
				key: "post-image-upload-limit",
				content: `You can upload up to ${maxImages} image${
					maxImages === 1 ? "" : "s"
				} per post. ${result.rejectedCount} image${
					result.rejectedCount === 1 ? " was" : "s were"
				} not added.`,
			});
		}

		return result.fileList;
	};

	// Counter turns amber as the limit nears and red when nearly exhausted.
	const counterStyle = (remaining: number) =>
		remaining < 10
			? { color: "#c0392b" }
			: remaining < 20
			? { color: "#e67e22" }
			: undefined;

	const handleRemoveExistingImage = (id: number) => {
		setDeletedImageIds((prev) => [...prev, id]);
		setExistingImages((prev) => prev.filter((img) => img.id !== id));
	};

	// Remove a newly-selected (not-yet-uploaded) photo from the antd fileList.
	const handleRemoveSelectedImage = (uid: string) => {
		const current: any[] = form.getFieldValue("image") || [];
		form.setFieldsValue({
			image: current.filter((f: any) => f.uid !== uid),
		});
	};

	// Resolve a preview URL for an antd Upload file (object URL for a freshly
	// picked File, or an existing thumbUrl/url if present).
	const getSelectedImageSrc = (file: any): string => {
		if (file?.thumbUrl) return file.thumbUrl;
		if (file?.url) return file.url;
		const raw = file?.originFileObj ?? file;
		return raw instanceof Blob ? URL.createObjectURL(raw) : "";
	};

	useEffect(() => {
		if (!showPostForm) return;
		if (!form) return;

		if (editPostData?.community_post) {
			const { community_post, member_types, groups, tags } = editPostData;
			const { title, video_link, expiration_date, images } =
				community_post;

			if (contentRef.current)
				contentRef.current.scrollIntoView({
					behavior: "smooth",
				});

			form.setFieldsValue({
				title,
				details: storedToQuill(editPostData.community_post.description),
				link: video_link,
				audienceCheckboxGroup: member_types
					? member_types
							.filter(
								(member_type: any) => member_type.is_selected,
							)
							.map((member_type: any) => member_type.name)
					: [],
				groupsCheckboxGroup: groups
					? groups
							.filter((group: any) => group.is_selected)
							.map((group: any) => group.id)
					: [],
				tagsCheckboxGroup: tags
					? tags
							.filter((tag: any) => tag.is_selected)
							.map((tag: any) => tag.name)
					: [],
				expiryDate: expiration_date ? moment(expiration_date) : null,
			});
			setTitleNoOfCharactersRemaining(
				TITLE_MAX_NO_OF_CHARACTERS - title.length,
			);
			setDescriptionNoOfCharactersRemaining(
				DESCRIPTION_MAX_NO_OF_CHARACTERS -
					(community_post.description || "").replace(/<[^>]*>/g, "")
						.length,
			);
			setExistingImages(
				(images || []).map((img: any) => ({
					id: img.id,
					image: img.image,
				})),
			);
			setDeletedImageIds([]);
			// A post with a video link opens on the Video tab; otherwise Photos.
			setMediaType(video_link ? "video" : "image");
		} else {
			setTitleNoOfCharactersRemaining(TITLE_MAX_NO_OF_CHARACTERS);
			setDescriptionNoOfCharactersRemaining(
				DESCRIPTION_MAX_NO_OF_CHARACTERS,
			);
			setMediaType("image");
			form.resetFields();
			form.setFieldValue("audienceCheckboxGroup", [authUserMembership]);
		}
	}, [form, editPostData, authUserMembership, showPostForm]);

	return (
		<>
			{!hideEnablePostFormComponent && (
				<div
					key={`enable-post-form-${uniqueKey}`}
					style={showPostForm ? { display: "none" } : undefined}
				>
					<EnablePostForm handleShowPostForm={handleShowPostForm} />
				</div>
			)}
			<Modal
				key={`post-form-${uniqueKey}`}
				open={showPostForm}
				onCancel={handleHidePostForm}
				title={
					editPostData?.community_post ? "Edit post" : "Create post"
				}
				maskClosable={false}
				centered
				width={640}
				className={styles.postModal}
				bodyStyle={{
					overflowY: "auto",
					maxHeight: "calc(100vh - 240px)",
				}}
				footer={
					<div className={styles.formFooter}>
						{tagValidationMessage && (
							<div className={styles.tagValidationAlert} role="alert">
								{tagValidationMessage}
							</div>
						)}
						<div className={styles.formFooterActions}>
							<Button
								key="cancel"
								onClick={handleHidePostForm}
								disabled={submitting}
								className={styles.cancelButton}
							>
								{"Cancel"}
							</Button>
							<Button
								key="submit"
								type="primary"
								loading={submitting}
								className={styles.publishButton}
								onClick={() => form.submit()}
							>
								{editPostData?.community_post ? "Save" : "Publish post"}
							</Button>
						</div>
					</div>
				}
			>
				<div className={styles.postFormScope} ref={contentRef}>
					<Form
						name={`${uniqueKey}-post-form`}
						form={form}
						layout="vertical"
						onFinish={onFinish}
						onFinishFailed={handleFinishFailed}
						initialValues={initialValues}
						onValuesChange={onValuesChange}
					>
						<Row>
							<Col span={24}>
								<div
									className={classNames(
										styles.fieldLabel,
										styles.fieldLabelRequired,
									)}
								>
									{"Post title"}
								</div>
								<Form.Item
									name="title"
									rules={[
										{
											required: true,
											message: "Post title is required",
										},
									]}
								>
									<Input
										onChange={handleChangePostTitle}
										size="middle"
										placeholder={"What's this post about?"}
										maxLength={50}
									/>
								</Form.Item>
								<div
									className={styles.noOfCharactersRemaining}
									style={counterStyle(
										titleNoOfCharactersRemaining,
									)}
								>
									{!(titleNoOfCharactersRemaining === 0)
										? `${titleNoOfCharactersRemaining} ${
												titleNoOfCharactersRemaining > 1
													? "characters"
													: "character"
										  }  remaining`
										: "Limit reached"}
								</div>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<div
									className={classNames(
										styles.fieldLabel,
										styles.fieldLabelRequired,
									)}
								>
									{"Message"}
								</div>
								<Form.Item
									name="details"
									rules={[
										{
											validator: (
												_: any,
												val: string,
											) => {
												// #1448 Description is optional once a photo is
												// attached (new upload or an existing edit image).
												const hasImages =
													(form.getFieldValue("image")
														?.length || 0) > 0 ||
													existingImages.length > 0;
												const hasText =
													!!val &&
													val
														.replace(
															/<(.|\n)*?>/g,
															"",
														)
														.trim().length > 0;
												return hasImages ||
													hasText ||
													(!!val && /<img/i.test(val))
													? Promise.resolve()
													: Promise.reject(
															new Error(
																"Post description is required",
															),
													  );
											},
										},
									]}
								>
									<RichPostEditor
										members={users || []}
										placeholder={
											"Posts with detailed descriptions get more views and replies."
										}
										onImageUpload={handleUploadHtmlImage}
									/>
								</Form.Item>
								<div
									ref={suggestionsPortalHostRef}
									className={
										styles.divPositionRelativeWrapper
									}
								/>
								<div
									className={styles.noOfCharactersRemaining}
									style={counterStyle(
										descriptionNoOfCharactersRemaining,
									)}
								>
									{!(descriptionNoOfCharactersRemaining === 0)
										? `${descriptionNoOfCharactersRemaining} ${
												descriptionNoOfCharactersRemaining >
												1
													? "characters"
													: "character"
										  }  remaining`
										: "Limit reached"}
								</div>
							</Col>
						</Row>
						<Row style={{ marginTop: 18 }}>
							<Col span={24}>
								<div className={styles.fieldLabel}>
									{"Media (optional)"}
								</div>
								{/* Mockup-style media: photo / video icon toggle,
								    no drag-drop box and no radio group. */}
								<div className={styles.mediaGrid}>
									{/* #1449 The "Upload images" tile IS the picker — clicking it opens
									    the file dialog directly. The separate "Upload" tile was redundant. */}
									<Form.Item
										name="image"
										valuePropName="fileList"
										getValueFromEvent={normFile}
										noStyle
									>
										<Upload
											accept="image/*"
											multiple
											beforeUpload={() => false}
											showUploadList={false}
											className={styles.mediaTileUpload}
										>
											<div
												title="Upload images"
												className={classNames(
													styles.mediaTileBtn,
													{
														[styles.mediaTileActive]:
															mediaType ===
															"image",
													},
												)}
												onClick={() => {
													setMediaType("image");
													form.setFieldsValue({
														link: undefined,
													});
												}}
											>
												<UploadOutlined
													className={
														styles.mediaTileIcon
													}
												/>
												<span
													className={
														styles.mediaTileLabel
													}
												>
													{"Upload images"}
												</span>
												<span
													className={
														styles.mediaTileSub
													}
												>{`Up to ${maxImages}`}</span>
											</div>
										</Upload>
									</Form.Item>
									<button
										type="button"
										title="Add video link"
										className={classNames(
											styles.mediaTileBtn,
											{
												[styles.mediaTileActive]:
													mediaType === "video",
											},
										)}
										onClick={() => {
											setMediaType("video");
											form.setFieldsValue({ image: [] });
										}}
									>
										<YoutubeOutlined
											className={styles.mediaTileIcon}
										/>
										<span className={styles.mediaTileLabel}>
											{"Video link"}
										</span>
										<span className={styles.mediaTileSub}>
											{"YouTube / Vimeo"}
										</span>
									</button>
								</div>
								{mediaType === "video" && (
									<Form.Item
										name="link"
										style={{ marginTop: 10 }}
										rules={[
											{
												pattern:
													// eslint-disable-next-line no-useless-escape
													/^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be|vimeo\.com\/))(\/(?:[\w\-]+\?v=|embed\/|[0-9]+|v\/)?)?([\w\-]+)(\S+)?$/gim,
												message:
													"Invalid URL provided, please insert a YouTube or a Vimeo link.",
											},
										]}
									>
										<Input
											className={styles.link}
											size="middle"
											prefix={<YoutubeOutlined />}
											placeholder={
												"Add YouTube or Vimeo link to upload a video"
											}
										/>
									</Form.Item>
								)}
								{mediaType === "image" && (
									<div className={styles.photoArea}>
										{/* Mobile-style preview of selected photos:
										    larger rounded thumbnails, each with a
										    white "remove" pill in the corner. Covers
										    both already-saved (edit) images and
										    freshly-picked files. */}
										{(existingImages.length > 0 ||
											(watchImages &&
												watchImages.length > 0)) && (
											<div className={styles.previewRow}>
												{existingImages.map((img) => (
													<div
														key={`existing-${img.id}`}
														className={
															styles.previewItem
														}
													>
														<button
															type="button"
															className={
																styles.previewRemove
															}
															onClick={() =>
																handleRemoveExistingImage(
																	img.id,
																)
															}
														>
															{"Remove"}
														</button>
														<img
															alt=""
															src={img.image}
															className={
																styles.previewImg
															}
														/>
													</div>
												))}
												{(watchImages || []).map(
													(file: any) => (
														<div
															key={`new-${file.uid}`}
															className={
																styles.previewItem
															}
														>
															<button
																type="button"
																className={
																	styles.previewRemove
																}
																onClick={() =>
																	handleRemoveSelectedImage(
																		file.uid,
																	)
																}
															>
																{"Remove"}
															</button>
															<img
																alt=""
																src={getSelectedImageSrc(
																	file,
																)}
																className={
																	styles.previewImg
																}
															/>
														</div>
													),
												)}
											</div>
										)}
									</div>
								)}
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								{
									<>
										{communityTags.memberTypes &&
											communityTags.memberTypes.length >
												0 && (
												<>
													<div
														ref={tagSectionRef}
														className={styles.tags}
													>
														<CheckboxGroupView
															rules={[
																{
																	required:
																		true,
																	message:
																		"'Who should see this post?' is required",
																},
															]}
															titleNode={
																<>
																	{
																		"Visible to "
																	}
																	<span
																		className={
																			styles.labelRequiredMark
																		}
																	>
																		{"*"}
																	</span>
																</>
															}
															checkAllTitle={
																"All Members"
															}
															category="audience"
															options={communityTags.memberTypes.map(
																(item: any) =>
																	item.name,
															)}
															checkedValues={
																watch
															}
															disabled={
																authUserMembership
															}
															setValue={
																form.setFieldValue
															}
															key={"audience"}
														/>
														<div
															className={
																styles.audienceSummary
															}
														>
															{watch &&
															watch.length > 0 ? (
																<>
																	{
																		"Visible to: "
																	}
																	<span
																		className={
																			styles.audienceSummaryValue
																		}
																	>
																		{watch.join(
																			", ",
																		)}
																	</span>
																</>
															) : (
																<span
																	className={
																		styles.audienceSummaryEmpty
																	}
																>
																	{
																		"No audience selected"
																	}
																</span>
															)}
														</div>
													</div>
												</>
											)}

										{!currentGroupId &&
											groups &&
											groups.length > 0 && (
												<>
													<div
														className={styles.tags}
													>
														<CheckboxGroupView
															title={"GROUPS"}
															checkAllTitle={
																"All Groups"
															}
															category="groups"
															options={groups.map(
																(
																	item: any,
																) => ({
																	label: item.name,
																	value: item.id,
																}),
															)}
															key={"groups"}
														/>
													</div>
												</>
											)}
										{communityTags.tags &&
											communityTags.tags.length > 0 && (
												<>
													<div
														className={styles.tags}
													>
														<CheckboxGroupView
															rules={[
																() => ({
																	validator(
																		rule: any,
																		value: any,
																	) {
																		if (
																			value &&
																			value.length >=
																				1 &&
																			value.length <=
																				3
																		) {
																			return Promise.resolve();
																		} else {
																			return Promise.reject(
																				"Please choose at least one tag and three tags at most!",
																			);
																		}
																	},
																}),
															]}
															checkAllTitle={""}
															titleNode={
																<>
																	{"Tags "}
																	<span
																		className={
																			styles.labelRequiredMark
																		}
																	>
																		{"*"}
																	</span>
																	<span
																		className={
																			styles.labelMuted
																		}
																	>
																		{
																			" (choose up to 3)"
																		}
																	</span>
																</>
															}
															rightNode={
																<span
																	className={
																		styles.tagCounter
																	}
																>
																	{`${
																		watchTags?.length ??
																		0
																	} / 3 selected`}
																</span>
															}
															category="tags"
															key={"tags"}
															options={(
																communityTags.tags as {
																	id: number;
																	name: string;
																	is_selected: boolean;
																}[]
															).map(
																(
																	item: any,
																) => ({
																	label: item.name,
																	value: item.name,
																	// Once 3 tags are chosen, grey out the rest
																	// so the user can't exceed the limit.
																	disabled:
																		(watchTags?.length ??
																			0) >=
																			3 &&
																		!watchTags?.includes(
																			item.name,
																		),
																}),
															)}
														/>
													</div>
												</>
											)}
									</>
								}
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<div className={styles.expiryCard}>
									<div>
										<div className={styles.expiryLabel}>
											{"Set expiry date"}
										</div>
										<div className={styles.expiryHint}>
											{
												"Optional: hide this post automatically after a date"
											}
										</div>
									</div>
									<Form.Item name="expiryDate" noStyle>
										<DatePicker
											className={styles.datePicker}
											disabledDate={disabledDate}
										/>
									</Form.Item>
								</div>
							</Col>
						</Row>
					</Form>
				</div>
			</Modal>
		</>
	);
};

export default memo(PostForm);
